"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { renderFormattedText } from "@/components/health-tech/formatHealthTechText";

export interface EcosystemLabels {
  hubTitle: string;
  hubSub: string;
  clearingHouse: string;
  healthPlans: string;
  hie: string;
  hisp: string;
  thirdPartyEhrs: string;
  dmes: string;
  rcm: string;
  lis: string;
  pharmacy: string;
  ris: string;
  vnaPacs: string;
  portalsApps: string;
  wearables: string;
  registries: string;
  devices: string;
}

export interface EcosystemData {
  eyebrow?: string;
  title: string;
  boldTitle?: string;
  description: string;
  labels: EcosystemLabels;
}

const EASE = [0.16, 1, 0.3, 1] as const;

type Box = { x: number; y: number; w: number; h: number };

/**
 * Box geometry — a fixed schematic, not driven by data (only the text inside
 * is localized). Every edge is orthogonal (no diagonals) and each cluster of
 * parallel lines is routed through its own lane so nothing overlaps or cuts
 * through a box — see the per-edge comments below for the lane logic.
 */
function useLayout(labels: EcosystemLabels) {
  const boxes: Record<string, Box & { label: string; sub?: string; accent?: boolean }> = {
    clearingHouse: { x: 30, y: 20, w: 190, h: 56, label: labels.clearingHouse },
    healthPlans: { x: 30, y: 110, w: 190, h: 56, label: labels.healthPlans },
    hie: { x: 30, y: 200, w: 190, h: 56, label: labels.hie },
    hisp: { x: 30, y: 290, w: 190, h: 56, label: labels.hisp },
    thirdPartyEhrs: { x: 30, y: 380, w: 190, h: 56, label: labels.thirdPartyEhrs },
    dmes: { x: 30, y: 470, w: 190, h: 56, label: labels.dmes },

    rcm: { x: 300, y: 20, w: 160, h: 56, label: labels.rcm },

    // Centered on the diagram's true content midpoint (x=615, halfway
    // between the left column at x=30 and the right column ending at
    // x=1200) rather than left over from an earlier, wider layout.
    hub: { x: 515, y: 237, w: 200, h: 72, label: labels.hubTitle, sub: labels.hubSub, accent: true },

    // Portals/Apps + Wearables shifted left so Wearables' right edge lines
    // up flush with Registries and Devices below — one clean right-hand
    // column instead of a staggered edge.
    portalsApps: { x: 780, y: 20, w: 190, h: 56, label: labels.portalsApps },
    wearables: { x: 1020, y: 20, w: 180, h: 56, label: labels.wearables },
    registries: { x: 1010, y: 110, w: 190, h: 56, label: labels.registries },
    devices: { x: 1010, y: 220, w: 190, h: 56, label: labels.devices },

    // Bottom row sits directly under the hub — pharmacy shares the hub's
    // center x so its connector is a single straight drop, no elbow needed.
    // VNA/PACS sits beside RIS (not stacked below it) to keep the whole
    // diagram flat enough to read in one screen, no vertical scrolling.
    // Row is pushed down to align with the bottom of the left-hand column
    // instead of hugging the hub, so it fills the card evenly.
    lis: { x: 325, y: 470, w: 150, h: 56, label: labels.lis },
    pharmacy: { x: 540, y: 470, w: 150, h: 56, label: labels.pharmacy },
    ris: { x: 745, y: 470, w: 150, h: 56, label: labels.ris },
    vnaPacs: { x: 915, y: 470, w: 150, h: 56, label: labels.vnaPacs },
  };

  const cx = (b: Box) => b.x + b.w / 2;
  const cy = (b: Box) => b.y + b.h / 2;
  const right = (b: Box) => ({ x: b.x + b.w, y: cy(b) });
  const left = (b: Box) => ({ x: b.x, y: cy(b) });
  const top = (b: Box) => ({ x: cx(b), y: b.y });
  const bottom = (b: Box) => ({ x: cx(b), y: b.y + b.h });

  const edges: {
    id: string;
    points: [number, number][];
    label?: string;
    labelAt?: [number, number];
    /** Defaults to "start". Use "end" / "middle" when the label sits right
     * next to a box, so long text grows away from it instead of over it. */
    labelAnchor?: "start" | "middle" | "end";
  }[] = [
    // Left chain — Clearing House → Health Plans → HIE, each a plain vertical/horizontal hop.
    {
      id: "ch-rcm",
      points: [
        [right(boxes.clearingHouse).x, right(boxes.clearingHouse).y],
        [left(boxes.rcm).x, left(boxes.rcm).y],
      ],
      label: "X12 EDI",
      labelAt: [225, 38],
    },
    {
      id: "ch-hp",
      points: [
        [bottom(boxes.clearingHouse).x, bottom(boxes.clearingHouse).y],
        [top(boxes.healthPlans).x, top(boxes.healthPlans).y],
      ],
      label: "X12 EDI",
      labelAt: [135, 96],
    },
    {
      id: "hp-hie",
      points: [
        [bottom(boxes.healthPlans).x, bottom(boxes.healthPlans).y],
        [top(boxes.hie).x, top(boxes.hie).y],
      ],
      label: "IHE",
      labelAt: [135, 186],
    },
    // Shared bus — HIE / HISP / 3rd-party EHRs / DMEs all reach the hub over
    // custom APIs. Each feeds a single vertical spine at x=255 which then
    // makes one horizontal run straight into the hub's left edge.
    {
      id: "bus-hie",
      points: [
        [right(boxes.hie).x, right(boxes.hie).y],
        [255, right(boxes.hie).y],
      ],
    },
    {
      id: "bus-hisp",
      points: [
        [right(boxes.hisp).x, right(boxes.hisp).y],
        [255, right(boxes.hisp).y],
      ],
    },
    {
      id: "bus-3p",
      points: [
        [right(boxes.thirdPartyEhrs).x, right(boxes.thirdPartyEhrs).y],
        [255, right(boxes.thirdPartyEhrs).y],
      ],
    },
    {
      id: "bus-dme",
      points: [
        [right(boxes.dmes).x, right(boxes.dmes).y],
        [255, right(boxes.dmes).y],
      ],
    },
    {
      id: "bus-spine",
      points: [
        [255, right(boxes.hie).y],
        [255, right(boxes.dmes).y],
      ],
    },
    {
      id: "bus-hub",
      points: [
        [255, cy(boxes.hub)],
        [left(boxes.hub).x, cy(boxes.hub)],
      ],
      label: "Custom APIs — REST / SOAP",
      labelAt: [270, cy(boxes.hub) - 12],
    },
    // Hub — TOP edge: RCM arrives on the left half, Portals/Apps departs on
    // the right half, so the two lanes never share an x.
    {
      id: "rcm-hub",
      points: [
        [bottom(boxes.rcm).x, bottom(boxes.rcm).y],
        [bottom(boxes.rcm).x, 170],
        [cx(boxes.hub) - 60, 170],
        [cx(boxes.hub) - 60, top(boxes.hub).y],
      ],
      label: "HL7",
      labelAt: [392, 162],
    },
    {
      id: "hub-portals",
      points: [
        [cx(boxes.hub) + 60, top(boxes.hub).y],
        [cx(boxes.hub) + 60, cy(boxes.portalsApps)],
        [left(boxes.portalsApps).x, left(boxes.portalsApps).y],
      ],
      // Long label, short lane — anchor it to grow leftward from just
      // before Portals/Apps' left edge instead of overflowing into the box.
      label: "HL7 FHIR / USCDI",
      labelAt: [772, 40],
      labelAnchor: "end",
    },
    {
      id: "wearables-portals",
      points: [
        [left(boxes.wearables).x, left(boxes.wearables).y],
        [right(boxes.portalsApps).x, right(boxes.portalsApps).y],
      ],
    },
    // Hub — RIGHT edge: Registries departs upper, Devices departs lower;
    // each rides its own lane (x=800 / x=815) so the two never touch even
    // though their vertical spans overlap.
    {
      id: "hub-registries",
      points: [
        [right(boxes.hub).x, cy(boxes.hub) - 16],
        [800, cy(boxes.hub) - 16],
        [800, cy(boxes.registries)],
        [left(boxes.registries).x, left(boxes.registries).y],
      ],
    },
    {
      id: "hub-devices",
      points: [
        [right(boxes.hub).x, cy(boxes.hub) + 16],
        [815, cy(boxes.hub) + 16],
        [815, cy(boxes.devices)],
        [left(boxes.devices).x, cy(boxes.devices)],
      ],
      label: "IEEE 11073 / BLE",
      labelAt: [821, cy(boxes.devices) - 10],
    },
    // Hub — BOTTOM edge: LIS / Pharmacy / RIS each get their own lane, all
    // turning above the row (before any box starts) so no horizontal leg
    // ever cuts through a neighboring box.
    {
      id: "hub-lis",
      points: [
        [cx(boxes.hub) - 40, bottom(boxes.hub).y],
        [cx(boxes.hub) - 40, 405],
        [top(boxes.lis).x, 405],
        [top(boxes.lis).x, top(boxes.lis).y],
      ],
      label: "HL7",
      labelAt: [443, 396],
    },
    {
      id: "hub-pharmacy",
      points: [
        [cx(boxes.hub), bottom(boxes.hub).y],
        [top(boxes.pharmacy).x, top(boxes.pharmacy).y],
      ],
    },
    {
      id: "hub-ris",
      points: [
        [cx(boxes.hub) + 40, bottom(boxes.hub).y],
        [cx(boxes.hub) + 40, 405],
        [top(boxes.ris).x, 405],
        [top(boxes.ris).x, top(boxes.ris).y],
      ],
      label: "HL7, C-CDA",
      labelAt: [695, 396],
    },
    {
      id: "vna-ris",
      points: [
        [left(boxes.vnaPacs).x, left(boxes.vnaPacs).y],
        [right(boxes.ris).x, right(boxes.ris).y],
      ],
      // RIS and VNA/PACS sit close together, so the connector is short —
      // the label goes above both boxes, centered over the gap, rather
      // than on the line itself where it would sit on top of a box.
      label: "DICOM",
      labelAt: [(right(boxes.ris).x + left(boxes.vnaPacs).x) / 2, boxes.ris.y - 12],
      labelAnchor: "middle",
    },
  ];

  return { boxes, edges, viewW: 1230, viewH: 560 };
}

function polylinePath(points: [number, number][]) {
  return points.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`).join(" ");
}

export function IntegrationEcosystemDiagram({ data }: { data: EcosystemData }) {
  const prefersReduced = useReducedMotion();
  const { boxes, edges, viewW, viewH } = useLayout(data.labels);

  const mobileGroups: { title: string; protocol: string; items: string[] }[] = [
    {
      title: `${data.labels.clearingHouse} / ${data.labels.healthPlans} / ${data.labels.rcm}`,
      protocol: "X12 EDI · HL7",
      items: [data.labels.clearingHouse, data.labels.healthPlans, data.labels.rcm],
    },
    {
      title: `${data.labels.hie} / ${data.labels.hisp}`,
      protocol: "IHE · DIRECT · Custom APIs",
      items: [data.labels.hie, data.labels.hisp, data.labels.thirdPartyEhrs, data.labels.dmes],
    },
    {
      title: `${data.labels.portalsApps} / ${data.labels.registries}`,
      protocol: "HL7 FHIR / USCDI",
      items: [data.labels.portalsApps, data.labels.wearables, data.labels.registries],
    },
    {
      title: `${data.labels.lis} / ${data.labels.ris}`,
      protocol: "HL7 · NCPDP · DICOM · IEEE",
      items: [data.labels.lis, data.labels.pharmacy, data.labels.ris, data.labels.vnaPacs, data.labels.devices],
    },
  ];

  return (
    <section className="relative z-10 bg-transparent px-5 py-24 text-foreground sm:px-8 md:py-36 lg:px-12">
      <div className="mx-auto max-w-[88rem]">
        <div className="max-w-2xl">
          {data.eyebrow && (
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-foreground/50">
              {data.eyebrow}
            </p>
          )}
          <h2 className="mt-3 font-display text-balance text-3xl font-semibold leading-[1.08] tracking-[-0.02em] md:text-4xl">
            {data.title} {data.boldTitle}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-foreground/80 md:text-lg">
            {renderFormattedText(data.description)}
          </p>
        </div>

        {/* Desktop / tablet: full schematic, scaled to fit the card width in
            one view — no horizontal scrolling. Same glass-card recipe used
            on the homepage (why-choose.tsx / services.tsx / moving-cards.tsx). */}
        <motion.div
          className="mt-14 hidden overflow-hidden rounded-2xl border border-orange-500/20 bg-card/70 p-6 backdrop-blur-sm md:block"
          initial={prefersReduced ? false : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <svg
            viewBox={`0 0 ${viewW} ${viewH}`}
            className="h-auto w-full"
            role="img"
            aria-label={`${data.title} ${data.boldTitle ?? ""}`}
          >
            <defs>
              <marker
                id="ecosystem-arrow"
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="7"
                markerHeight="7"
                orient="auto-start-reverse"
              >
                <path d="M0,0 L10,5 L0,10 z" className="fill-foreground/40" />
              </marker>
            </defs>

            {edges.map((edge) => (
              <g key={edge.id}>
                <path
                  d={polylinePath(edge.points)}
                  fill="none"
                  className="stroke-foreground/25"
                  strokeWidth={1.5}
                  strokeLinejoin="round"
                  markerEnd="url(#ecosystem-arrow)"
                />
                {edge.label && edge.labelAt && (
                  <text
                    x={edge.labelAt[0]}
                    y={edge.labelAt[1]}
                    textAnchor={edge.labelAnchor ?? "start"}
                    className="fill-foreground/55 font-sans"
                    style={{ fontSize: 13, letterSpacing: "0.02em" }}
                  >
                    {edge.label}
                  </text>
                )}
              </g>
            ))}

            {Object.entries(boxes).map(([id, box]) => (
              <g key={id}>
                <rect
                  x={box.x}
                  y={box.y}
                  width={box.w}
                  height={box.h}
                  rx={12}
                  className={box.accent ? "fill-orange-500 stroke-orange-500" : "fill-card stroke-border"}
                  strokeWidth={1.5}
                />
                <text
                  x={box.x + box.w / 2}
                  y={box.sub ? box.y + box.h / 2 - 8 : box.y + box.h / 2 + 5}
                  textAnchor="middle"
                  className={box.accent ? "fill-white font-display" : "fill-foreground font-display"}
                  style={{ fontSize: 15, fontWeight: 600 }}
                >
                  {box.label}
                </text>
                {box.sub && (
                  <text
                    x={box.x + box.w / 2}
                    y={box.y + box.h / 2 + 14}
                    textAnchor="middle"
                    className="fill-white/80"
                    style={{ fontSize: 11 }}
                  >
                    {box.sub}
                  </text>
                )}
              </g>
            ))}
          </svg>
        </motion.div>

        {/* Mobile: grouped chip list — the full schematic doesn't survive a narrow viewport legibly. */}
        <div className="mt-12 grid gap-6 md:hidden">
          <div className="rounded-2xl border border-orange-500/20 bg-card/70 px-5 py-4 backdrop-blur-sm">
            <p className="font-display text-lg font-semibold text-foreground">{data.labels.hubTitle}</p>
            <p className="mt-0.5 text-sm text-foreground/60">{data.labels.hubSub}</p>
          </div>
          {mobileGroups.map((group) => (
            <div key={group.title} className="border-t border-border pt-5">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-orange-500">
                {group.protocol}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {group.items.map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-border px-3.5 py-1.5 text-xs font-medium text-foreground/70"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default IntegrationEcosystemDiagram;
